import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../../../styles/theme';
import { XMarkIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import DataBrowser from '../DataBrowser';
import toast from 'react-hot-toast';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  z-index: ${theme.zIndex.modal};
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 2rem 1rem;
  overflow-y: auto;
`;

const Modal = styled.div`
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: 12px;
  max-width: 720px;
  width: 100%;
  padding: 1.25rem;
  margin-bottom: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const Title = styled.h3`
  margin: 0;
  color: ${theme.colors.text.primary};
  font-size: 1.125rem;
`;

const CloseBtn = styled.button`
  background: transparent;
  border: none;
  color: ${theme.colors.text.secondary};
  cursor: pointer;
  padding: 4px;
`;

const Label = styled.label`
  display: block;
  font-size: 0.8rem;
  color: ${theme.colors.text.secondary};
  margin-bottom: 4px;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 10px;
  border-radius: 6px;
  border: 1px solid ${theme.colors.border.primary};
  background: ${theme.colors.background.paper};
  color: ${theme.colors.text.primary};
  font-size: 14px;
`;

const Select = styled.select`
  width: 100%;
  padding: 8px 10px;
  border-radius: 6px;
  border: 1px solid ${theme.colors.border.primary};
  background: ${theme.colors.background.paper};
  color: ${theme.colors.text.primary};
  font-size: 14px;
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: 10px;
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const PumpCard = styled.div`
  border: 1px solid ${theme.colors.border.secondary};
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 12px;
  background: ${theme.colors.background.primary};
`;

const BtnRow = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 12px;
`;

const Btn = styled.button`
  padding: 8px 14px;
  border-radius: 8px;
  border: 1px solid ${theme.colors.border.primary};
  background: ${(p) => (p.$primary ? theme.colors.primary.main : theme.colors.background.paper)};
  color: ${(p) => (p.$primary ? '#fff' : theme.colors.text.primary)};
  cursor: pointer;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const Help = styled.p`
  font-size: 12px;
  color: ${theme.colors.text.disabled};
  margin: 0 0 12px 0;
  line-height: 1.4;
`;

/** Demo JSON paths in backend/data/demo-signalk-data.json — forward (index 0) and aft (index 1+) */
const defaultPump = (index = 0) => ({
  id: typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `p-${Date.now()}`,
  name: index === 0 ? 'Forward bilge' : 'Aft bilge',
  paths:
    index === 0
      ? {
          running: 'electrical.switches.bilgePump',
          voltage: 'electrical.bilge.forward.voltage',
          current: 'electrical.bilge.forward.current',
        }
      : {
          running: 'electrical.switches.bilgePumpAft',
          voltage: 'electrical.bilge.aft.voltage',
          current: 'electrical.bilge.aft.current',
        },
  alarms: {
    frequency: {
      enabled: false,
      maxRuns: 5,
      windowMinutes: 60,
      notifyEmails: [],
    },
    continuousRun: {
      enabled: false,
      maxMinutes: 15,
      notifyEmails: [],
    },
  },
});

const BilgeMonitorSettingsModal = ({
  open,
  onClose,
  dashboardId,
  vesselId,
  initialConfig,
  onSaved,
  sections = [],
}) => {
  const [enabled, setEnabled] = useState(false);
  const [useDemoData, setUseDemoData] = useState(false);
  const [simulatePeriodicBilge, setSimulatePeriodicBilge] = useState(true);
  const [seasonStartMonth, setSeasonStartMonth] = useState(4);
  const [seasonStartDay, setSeasonStartDay] = useState(1);
  const [insertIndex, setInsertIndex] = useState(0);
  const [pumps, setPumps] = useState([]);
  const [pathPicker, setPathPicker] = useState(null);

  const sectionList = Array.isArray(sections) ? sections : [];
  const sectionIdsKey = sectionList.map((s) => s.id).join(',');

  useEffect(() => {
    if (!initialConfig) return;
    setEnabled(!!initialConfig.enabled);
    setUseDemoData(!!initialConfig.useDemoData);
    setSimulatePeriodicBilge(initialConfig.simulatePeriodicBilge !== false);
    setSeasonStartMonth(initialConfig.seasonStartMonth || 4);
    setSeasonStartDay(initialConfig.seasonStartDay || 1);
    const maxIdx = sectionList.length;
    let idx = initialConfig.insertIndex;
    if (idx == null || idx === '') idx = 0;
    else idx = Number(idx);
    if (!Number.isFinite(idx)) idx = 0;
    setInsertIndex(Math.max(0, Math.min(maxIdx, Math.floor(idx))));
    setPumps(
      Array.isArray(initialConfig.pumps) && initialConfig.pumps.length
        ? initialConfig.pumps.map((p, i) => ({
            ...defaultPump(i),
            ...p,
            paths: { ...defaultPump(i).paths, ...(p.paths || {}) },
            alarms: {
              frequency: { ...defaultPump(i).alarms.frequency, ...(p.alarms?.frequency || {}) },
              continuousRun: { ...defaultPump(i).alarms.continuousRun, ...(p.alarms?.continuousRun || {}) },
            },
          }))
        : []
    );
  }, [initialConfig, open, sectionList.length, sectionIdsKey]);

  if (!open) return null;

  const updatePump = (idx, patch) => {
    setPumps((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], ...patch };
      return next;
    });
  };

  const updatePumpPath = (idx, key, value) => {
    setPumps((prev) => {
      const next = [...prev];
      next[idx] = {
        ...next[idx],
        paths: { ...next[idx].paths, [key]: value },
      };
      return next;
    });
  };

  const updateAlarm = (idx, which, patch) => {
    setPumps((prev) => {
      const next = [...prev];
      next[idx] = {
        ...next[idx],
        alarms: {
          ...next[idx].alarms,
          [which]: { ...next[idx].alarms[which], ...patch },
        },
      };
      return next;
    });
  };

  const save = async () => {
    const maxIdx = sectionList.length;
    const safeInsert = Math.max(0, Math.min(maxIdx, Math.floor(Number(insertIndex) || 0)));
    const theme_config = {
      bilgeMonitor: {
        enabled,
        insertIndex: safeInsert,
        useDemoData,
        simulatePeriodicBilge: useDemoData ? simulatePeriodicBilge : false,
        seasonStartMonth,
        seasonStartDay,
        pumps,
      },
    };
    await onSaved(theme_config);
  };

  const placementOptions = (() => {
    const opts = [{ value: 0, label: 'Top — before first section' }];
    sectionList.forEach((s, i) => {
      const nm = (s.name || s.section_name || 'Section').replace(/"/g, "'");
      opts.push({ value: i + 1, label: `After "${nm}"` });
    });
    return opts;
  })();

  const selectValue = Math.min(insertIndex, sectionList.length);

  return (
    <Overlay onClick={onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>Bilge monitor settings</Title>
          <CloseBtn type="button" onClick={onClose} aria-label="Close">
            <XMarkIcon className="w-6 h-6" />
          </CloseBtn>
        </Header>
        <Help>
          Map each bilge pump to NMEA 2000 / SignalK paths (use Browse to open the data browser). The server records
          run cycles for graphs and season totals. Email alerts use your SMTP settings.
        </Help>
        <Label>
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />{' '}
          Enable bilge monitor section on this dashboard
        </Label>
        {enabled && (
          <div style={{ marginTop: 12 }}>
            <Label>Placement on dashboard (view mode)</Label>
            <Select
              value={selectValue}
              onChange={(e) => setInsertIndex(Number(e.target.value))}
            >
              {placementOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
            <Help style={{ marginTop: 6 }}>
              Sections are ordered as on the edit screen. Choose where the bilge monitor appears among them (default
              is top).
            </Help>
          </div>
        )}
        <Label style={{ marginTop: 8 }}>
          <input
            type="checkbox"
            checked={useDemoData}
            onChange={(e) => setUseDemoData(e.target.checked)}
          />{' '}
          Use sample data (demo SignalK tree + synthetic run history)
        </Label>
        <Help style={{ marginTop: 6 }}>
          When enabled, the API can use either <strong>periodic simulation</strong> (pumps cycle on/off on a timer so
          counters and run duration update) or <strong>static JSON</strong> if you turn simulation off below.
          Turn sample data off when your vessel feeds real SignalK so the server records actual cycles.
        </Help>
        {useDemoData && (
          <Label style={{ marginTop: 8 }}>
            <input
              type="checkbox"
              checked={simulatePeriodicBilge}
              onChange={(e) => setSimulatePeriodicBilge(e.target.checked)}
            />{' '}
            Simulate periodic pump runs (~10 min cycle: forward ~90s on, aft ~2 min on, offset)
          </Label>
        )}
        <Row style={{ marginTop: 12 }}>
          <div>
            <Label>Season start (month)</Label>
            <Input
              type="number"
              min={1}
              max={12}
              value={seasonStartMonth}
              onChange={(e) => setSeasonStartMonth(parseInt(e.target.value, 10) || 4)}
            />
          </div>
          <div>
            <Label>Season start (day)</Label>
            <Input
              type="number"
              min={1}
              max={31}
              value={seasonStartDay}
              onChange={(e) => setSeasonStartDay(parseInt(e.target.value, 10) || 1)}
            />
          </div>
        </Row>

        {pumps.map((p, idx) => (
          <PumpCard key={p.id}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Label style={{ marginBottom: 0 }}>Pump name</Label>
              <button
                type="button"
                style={{ background: 'none', border: 'none', color: theme.colors.error.main, cursor: 'pointer' }}
                onClick={() => setPumps((prev) => prev.filter((_, i) => i !== idx))}
              >
                <TrashIcon className="w-5 h-5" />
              </button>
            </div>
            <Input
              value={p.name}
              onChange={(e) => updatePump(idx, { name: e.target.value })}
              style={{ marginBottom: 10 }}
            />
            <Label>Running / status path (required)</Label>
            <div style={{ display: 'flex', gap: 8 }}>
              <Input
                value={p.paths.running || ''}
                onChange={(e) => updatePumpPath(idx, 'running', e.target.value)}
                placeholder="e.g. electrical.switches.bilgePump"
              />
              <Btn type="button" onClick={() => setPathPicker({ pumpIdx: idx, field: 'running' })}>
                Browse
              </Btn>
            </div>
            <Row style={{ marginTop: 10 }}>
              <div>
                <Label>Voltage path (optional)</Label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Input
                    value={p.paths.voltage || ''}
                    onChange={(e) => updatePumpPath(idx, 'voltage', e.target.value)}
                  />
                  <Btn type="button" onClick={() => setPathPicker({ pumpIdx: idx, field: 'voltage' })}>
                    Browse
                  </Btn>
                </div>
              </div>
              <div>
                <Label>Current path (optional)</Label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Input
                    value={p.paths.current || ''}
                    onChange={(e) => updatePumpPath(idx, 'current', e.target.value)}
                  />
                  <Btn type="button" onClick={() => setPathPicker({ pumpIdx: idx, field: 'current' })}>
                    Browse
                  </Btn>
                </div>
              </div>
            </Row>

            <div style={{ marginTop: 12, fontSize: 13, color: theme.colors.text.secondary }}>Alarms</div>
            <Label style={{ marginTop: 8 }}>
              <input
                type="checkbox"
                checked={!!p.alarms.frequency.enabled}
                onChange={(e) => updateAlarm(idx, 'frequency', { enabled: e.target.checked })}
              />{' '}
              Too many runs in a time window
            </Label>
            <Row>
              <div>
                <Label>Max runs</Label>
                <Input
                  type="number"
                  min={1}
                  value={p.alarms.frequency.maxRuns}
                  onChange={(e) => updateAlarm(idx, 'frequency', { maxRuns: parseInt(e.target.value, 10) || 1 })}
                />
              </div>
              <div>
                <Label>Window (minutes)</Label>
                <Input
                  type="number"
                  min={1}
                  value={p.alarms.frequency.windowMinutes}
                  onChange={(e) =>
                    updateAlarm(idx, 'frequency', { windowMinutes: parseInt(e.target.value, 10) || 1 })
                  }
                />
              </div>
            </Row>
            <Label>Notify emails (comma-separated)</Label>
            <Input
              value={(p.alarms.frequency.notifyEmails || []).join(', ')}
              onChange={(e) =>
                updateAlarm(idx, 'frequency', {
                  notifyEmails: e.target.value
                    .split(',')
                    .map((s) => s.trim())
                    .filter(Boolean),
                })
              }
            />

            <Label style={{ marginTop: 10 }}>
              <input
                type="checkbox"
                checked={!!p.alarms.continuousRun.enabled}
                onChange={(e) => updateAlarm(idx, 'continuousRun', { enabled: e.target.checked })}
              />{' '}
              Running continuously too long
            </Label>
            <Row>
              <div>
                <Label>Max continuous run (minutes)</Label>
                <Input
                  type="number"
                  min={1}
                  value={p.alarms.continuousRun.maxMinutes}
                  onChange={(e) =>
                    updateAlarm(idx, 'continuousRun', { maxMinutes: parseInt(e.target.value, 10) || 1 })
                  }
                />
              </div>
              <div>
                <Label>Notify emails (comma-separated)</Label>
                <Input
                  value={(p.alarms.continuousRun.notifyEmails || []).join(', ')}
                  onChange={(e) =>
                    updateAlarm(idx, 'continuousRun', {
                      notifyEmails: e.target.value
                        .split(',')
                        .map((s) => s.trim())
                        .filter(Boolean),
                    })
                  }
                />
              </div>
            </Row>
          </PumpCard>
        ))}

        <Btn type="button" onClick={() => setPumps((prev) => [...prev, defaultPump(prev.length)])}>
          <PlusIcon className="w-4 h-4" />
          Add bilge pump
        </Btn>

        <BtnRow>
          <Btn type="button" $primary onClick={save}>
            Save bilge settings
          </Btn>
          <Btn type="button" onClick={onClose}>
            Cancel
          </Btn>
        </BtnRow>
      </Modal>

      {pathPicker && vesselId && (
        <div
          style={{ position: 'fixed', zIndex: theme.zIndex.modal + 2, inset: 0, background: 'rgba(0,0,0,0.5)' }}
          onClick={() => setPathPicker(null)}
        >
          <div
            style={{
              position: 'absolute',
              top: '5%',
              left: '50%',
              transform: 'translateX(-50%)',
              width: 'min(900px, 96vw)',
              maxHeight: '85vh',
              overflow: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <DataBrowser
              vesselId={vesselId}
              dataSource="database"
              onPathSelect={(path) => {
                updatePumpPath(pathPicker.pumpIdx, pathPicker.field, path);
                toast.success(`Path: ${path}`);
                setPathPicker(null);
              }}
            />
          </div>
        </div>
      )}
    </Overlay>
  );
};

export default BilgeMonitorSettingsModal;
